<template>
  <TableWithEndlessScroll
    :columns="columns"
    :isRefreshing="isRefreshing"
    :loadingAdditionalData="loadingAdditionalData"
    :moreToLoad="moreToLoad"
    :renderActionColumn="true"
    :tableData="tableData"
    @cellClick="handleCellClick"
    @loadMore="handleLoadMore"
  >
    <template
      v-slot:expandedContent="props"
    >
      <div :class="$style.eventDetails">
        <ul :class="$style.nav">
          <li
            v-for="tab in [tabs.summary, tabs.json]"
            :key="tab"
          >
            <a
              :class="[$style.tabLink, {
                [$style.tabLinkActive]: shouldShowTab(props.record.id, tab),
              }]"
              @click="showTab(props.record.id, tab)"
            >
              {{ tab }}
            </a>
          </li>
        </ul>
        <div :class="$style.contentContainer">
          <EventSummary
            v-show="shouldShowTab(props.record.id, tabs.summary)"
            :record="props.record"
          />
          <!-- Keep pre content inline to prevent adding indentation to first bracket -->
          <pre
            v-show="shouldShowTab(props.record.id, tabs.json)"
            :class="$style.json"
          >{{ props.record }}</pre>
        </div>
      </div>
    </template>
    <template v-slot:emptyMessage>
      <p>
        <!-- Formatting the next line will result in space before the period -->
        <!-- eslint-disable-next-line-->
        <strong>Increase your time range to see more events</strong><strong v-if="areFiltersApplied"> or <a @click="handleClearClick">start over</a></strong><strong>.</strong>
      </p>
    </template>
  </TableWithEndlessScroll>
</template>

<script>
import { ItemTypeConsts, StoreModuleMap } from 'client/util/Constants';
import { isEqual } from 'lodash';
import { mapActions, mapState } from 'vuex';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import EventSummary from 'client/Insights/ActivityLog/EventSummary/EventSummary.vue';
import TableWithEndlessScroll from 'components/Tables/TableWithEndlessScroll.vue';

const insightsEventModule = StoreModuleMap[ItemTypeConsts.insightsEvent];

const tabs = {
  json: 'JSON',
  summary: 'Summary',
};

export default {
  name: 'InsightsEventsTable',
  components: {
    EventSummary,
    TableWithEndlessScroll,
  },

  props: {
    columns: {
      type: Array,
      default: () => [],
    },
    isRefreshing: {
      type: Boolean,
      default: false,
    },
    loadingAdditionalData: {
      type: Boolean,
      default: false,
    },
    moreToLoad: {
      type: Boolean,
      default: true,
    },
    tableData: {
      type: Array,
      default: () => [],
    },
  },

  data: () => ({
    tabShowingById: {},
    tabs,
  }),

  computed: {
    ...mapState(insightsEventModule, [
      'filters',
      'initialFilterState',
    ]),

    areFiltersApplied() {
      return !isEqual(this.initialFilterState, this.filters);
    },
  },

  methods: {
    ...mapActions(insightsEventModule, [
      'clearFilters',
      'setFilter',
      'setSelectedView',
    ]),

    handleCellClick(column, value) {
      this.setFilter({ field: column.name, value: [value] });
      this.$emit('filterChange');

      EventHelper.triggerActivityLogTableCellValueClicked({
        field: column.name,
        value,
      });
    },

    handleClearClick() {
      this.$emit('restoreDefaults');
    },

    handleLoadMore() {
      this.$emit('loadMore');
    },
    shouldShowTab(id, tabName) {
      // Default to summary view
      if (!this.tabShowingById[id]) {
        return tabName === tabs.summary;
      }
      return this.tabShowingById[id] === tabName;
    },
    showTab(id, tabName) {
      this.$set(this.tabShowingById, id, tabName);
    },
  },
};
</script>
<style scoped module>
@value (gradientDark2, gradientLight3) from '~css/colors.css';

.eventDetails {
  padding: 20px 30px;
}

.nav {
  margin: 0 !important; /* overriding legacy <ul> styles */
  padding: 0;
}

.nav li {
  display: inline-block;
  list-style-type: none;
}

.tabLink {
  color: gradientDark2;
  cursor: pointer;
  font-weight: 600;
  margin-right: 20px;
  padding-bottom: 4px;
}

.tabLink:focus,
.tabLink:hover {
  color: gradientDark2;
  text-decoration: none !important; /* overriding legacy <a> styles */
}

.tabLinkActive {
  border-bottom: 2px solid var(--jcPrimary);
  font-weight: bold;
}

.contentContainer {
  border-top: 1px solid gradientLight3;
  margin-top: 4px;
}

.json {
  border: none;
  border-radius: 0;
  font-family: var(--jc-font-monospace);
  font-size: 10px;
}

.jsonBlock {
  margin: 10px 30px 30px;
}
</style>
